import { RevealGroup, RevealItem, Reveal } from "@/lib/motion";
import { Section, SectionHeading, Tag } from "./primitives";
import { ECOSYSTEM, STAKING_TIERS, BUYBACK_POINTS } from "@/config/site";
import { Wallet, Search, Landmark, TerminalSquare, ShoppingBag, ArrowUpRight, Flame } from "lucide-react";

const ECO_ICONS = [Wallet, Search, Landmark, TerminalSquare, ShoppingBag];
const ECOSYSTEM_IMG =
  "https://images.unsplash.com/photo-1579548122080-c35fd6820ecb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3ODcwNzI2Mzh8MA&ixlib=rb-4.1.0&q=85";

export default function EcosystemSection() {
  return (
    <Section id="ecosystem">
      <SectionHeading
        eyebrow="Ecosystem"
        title="Building the Xenovia Ecosystem"
        lead="A suite of planned products designed to give XVPR real-world context. None of these are live yet — each is clearly marked as planned."
      />

      {/* Bento grid of planned products */}
      <RevealGroup className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-12">
        {ECOSYSTEM.map((e, i) => {
          const Icon = ECO_ICONS[i % ECO_ICONS.length];
          // Asymmetric layout: first card wide, rest balanced.
          const span = i === 0 ? "md:col-span-8" : i === 1 ? "md:col-span-4" : "md:col-span-4";
          return (
            <RevealItem key={e.title} className={span} data-testid={`ecosystem-${i}`}>
              <div className="group relative flex h-full min-h-[220px] flex-col justify-between overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-all duration-500 hover:border-white/20">
                {i === 0 && (
                  <>
                    <img
                      src={ECOSYSTEM_IMG}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover opacity-20 transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050508] via-[#050508]/80 to-transparent" />
                  </>
                )}
                <div className="relative flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-indigo-500/20 to-violet-500/10">
                    <Icon className="h-5 w-5 text-indigo-300" />
                  </span>
                  <Tag tone="planned">Planned</Tag>
                </div>
                <div className="relative">
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight text-white sm:text-xl">{e.title}</h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-gray-400">{e.body}</p>
                </div>
              </div>
            </RevealItem>
          );
        })}
      </RevealGroup>

      {/* Staking framework */}
      <div className="mt-28">
        <SectionHeading eyebrow="Planned Staking Framework" title="Stake for the Long Term" />
        <RevealGroup className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {STAKING_TIERS.map((s, i) => (
            <RevealItem key={s.period} data-testid={`staking-${i}`}>
              <div className="group flex h-full flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center transition-all duration-500 hover:border-indigo-400/30">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-500">{s.period}</div>
                <div className="font-display text-3xl font-black text-gradient-indigo sm:text-4xl">{s.mult}</div>
                <Tag tone="planned">Planned</Tag>
              </div>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>

      {/* Buyback & Burn */}
      <div className="mt-28">
        <Reveal className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-transparent p-8 sm:p-12">
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-violet-600/15 blur-[100px]" />
          <div className="relative grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2">
                <Flame className="h-4 w-4 text-violet-300" />
                <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-violet-300">Proposed Mechanism</span>
              </div>
              <h2 className="mt-5 font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl">
                Designed for Long-Term Scarcity
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-gray-400 sm:text-lg">
                Ecosystem revenue may be used to purchase XVPR on the open market. Purchased tokens
                may be transferred to an irrecoverable burn address. This is a proposed, future
                mechanism.
              </p>
            </div>
            <div className="grid gap-3 lg:col-span-5">
              {BUYBACK_POINTS.map((p) => (
                <div key={p} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4">
                  <span className="h-2 w-2 rounded-full bg-violet-400" />
                  <span className="font-display text-sm font-bold uppercase tracking-tight text-white">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
